import { useFlag } from '@mentimeter/flags/confidence/client';
import { useEffect } from 'react';
import { useLiveMomentStore, useOptionalPace } from '../../../data-mediation';
import { useSlide } from '../../../data-mediation/question';
import { getQrSidebarVisibility } from './get-qr-sidebar-visibility';

/**
 * Custom hook that manages participation details (QR code sidebar) visibility and behavior based on slide state.
 *
 * This hook handles the logic for displaying QR code sidebar and participation details based on:
 * - Current slide's QR code visibility setting
 * - Pacing state transitions (particularly view-answers/hide-answers flow)
 *
 * @featureFlag pi-qr-code-new-look.enabled (default: false)
 *
 * @returns An object containing:
 * - `shouldDisplayParticipationDetails` - Boolean indicating if participation details should be displayed
 * - `shouldHideRespondentsIndicator` - Boolean indicating if the respondents indicator should be hidden (same as shouldDisplayParticipationDetails)
 * - `shouldDisplayHiddenResultsNotification` - Boolean indicating if hidden results notification should be displayed (inverse of feature flag)
 */
export const useParticipationDetails = () => {
  const { value: isQrCodeNewLookFlagEnabled } = useFlag(
    'pi-qr-code-new-look.enabled',
    false,
  );

  const {
    staticContent: { type },
    qrCodeVisibility,
  } = useSlide();
  const paceState = useOptionalPace();
  const { isInQuizLobby, currentStep, previousStep } = paceState?.state ?? {};

  const isQrCodeSidebarVisible = useLiveMomentStore(
    (state) => state.isQrCodeSidebarVisible,
  );
  const setIsQrCodeSidebarVisible = useLiveMomentStore(
    (state) => state.setIsQrCodeSidebarVisible,
  );

  useEffect(() => {
    // early escape if you don't have the feature flag
    if (!isQrCodeNewLookFlagEnabled) {
      return;
    }
    // Get the next visibility state for the QR code sidebar
    const nextQrSidebarVisibility = getQrSidebarVisibility({
      type,
      qrCodeVisibility,
      isInQuizLobby,
      previousStep,
      currentStep,
    });

    // Set the QR code sidebar visibility state
    setIsQrCodeSidebarVisible(nextQrSidebarVisibility);
  }, [
    currentStep,
    isInQuizLobby,
    isQrCodeNewLookFlagEnabled,
    previousStep,
    qrCodeVisibility,
    setIsQrCodeSidebarVisible,
    type,
  ]);

  const shouldDisplayParticipationDetails =
    isQrCodeNewLookFlagEnabled &&
    (isQrCodeSidebarVisible === undefined
      ? qrCodeVisibility === 'visible'
      : isQrCodeSidebarVisible);

  return {
    shouldDisplayParticipationDetails,
    shouldHideRespondentsIndicator: shouldDisplayParticipationDetails,
    shouldDisplayHiddenResultsNotification: !isQrCodeNewLookFlagEnabled,
  };
};
